import Honeybadger from "@honeybadger-io/js";

// Configures the browser Honeybadger client as an import side effect. Imported
// once from entry.client so it runs before any reporting. The reporter instance
// used across the app lives in ~/reporter (isomorphic) and wraps this singleton.
Honeybadger.configure({
  apiKey: import.meta.env.VITE_HONEYBADGER_API_KEY,
  environment: import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE,
  revision: import.meta.env.VITE_REVISION,
  reportData: import.meta.env.MODE !== "test",
  // Third-party tracking scripts (Pinterest, etc.) produce unhandled rejections on bot
  // traffic that aren't actionable. All real errors are reported via captureException/
  // captureMessage or the onerror handler.
  enableUnhandledRejection: false,
});

// Honeybadger's and New Relic's own instrumentation bundles (reporter-*.js, browser-agent-*.js)
// monkey-patch XMLHttpRequest/fetch and are built under frontend-assets/ alongside our app code.
// A stack frame in one of them is never our code — it's the wrapper relaying a third-party
// script's own failure (e.g. a tracking pixel's XHR getting blocked).
const INSTRUMENTATION_BUNDLE = /\/(?:reporter|browser-agent)-[^/]+\.js/;

function hasAppFrame(stack: string): boolean {
  return stack
    .split("\n")
    .some(
      (line) =>
        line.includes("shirtspace.com/frontend-assets/") &&
        !INSTRUMENTATION_BUNDLE.test(line),
    );
}

export function shouldSuppressNotice(
  notice: { message?: string; stack?: string } | undefined,
): boolean {
  // Browser-level fetch failures ("Failed to fetch" in Chrome, "Load failed" in Safari,
  // "NetworkError when attempting to fetch resource." in Firefox) mean the TCP connection
  // died or was blocked before an HTTP response arrived. This is always a user-network or
  // proxy issue — never actionable from our code. Server-side drops show up in server logs.
  if (
    notice?.message === "Failed to fetch" ||
    notice?.message === "Load failed" ||
    notice?.message === "NetworkError when attempting to fetch resource."
  ) {
    return true;
  }

  // The XHR equivalent of the above: thrown synchronously from xhr.send() when the
  // underlying request is blocked (ad blockers, restrictive WebView network policies, etc.)
  // before it ever reaches the network.
  if (
    /^Failed to execute 'send' on 'XMLHttpRequest': Failed to load /.test(
      notice?.message ?? "",
    )
  ) {
    return true;
  }

  // React Router's lazy route discovery (/__manifest) throws "Error: <status> <statusText>"
  // when the fetch fails with an HTTP error. Bots that execute JS trigger this on every
  // product page load but get blocked before the response arrives.
  // Restricts to 4xx so that 5xx errors (real server bugs) still surface.
  if (
    /^4\d{2} /.test(notice?.message ?? "") &&
    notice?.stack?.includes("patchRoutesOnNavigation")
  ) {
    return true;
  }

  // Third-party tracking scripts (e.g. Pinterest ct/core.js) fail in restrictive WebViews
  // (e.g. NewsSapphire) when their XHR calls are blocked. These errors propagate through
  // New Relic's and Honeybadger's XHR wrappers into window.onerror.
  if (notice?.stack && !hasAppFrame(notice.stack)) {
    return true;
  }

  return false;
}

Honeybadger.beforeNotify((notice) => {
  if (shouldSuppressNotice(notice)) {
    return false;
  }
});
